/**
 * Shared utility for building the `filterBy` JSON query parameter from a
 * typed filter array. Almost every list API in the codebase follows the same
 * pattern: iterate filters, deduplicate values per key, serialize as JSON.
 *
 * The function is intentionally generic so that each call-site can customise
 * value extraction, key remapping, and special-case handling without
 * duplicating the core accumulation logic.
 */

export type FilterEntry = {
  filterBy: string;
  filterValue: { id: string; value: string; label?: string };
  filterValueKey?: string;
};

/**
 * Extracts the string value to accumulate for a given filter entry.
 * Default: `filter.filterValue.value`.
 */
export type FilterValueGetter = (filter: FilterEntry) => string | number;

/**
 * A handler that writes a non-standard value (e.g. `{ gte, lte }` range)
 * directly into the accumulator object. When a customHandler is provided for
 * a key, the default array-accumulation logic is skipped entirely.
 */
export type CustomFilterHandler = (filter: FilterEntry, filterObj: Record<string, unknown>) => void;

export interface BuildFilterByParamOptions {
  /** Controls which field to extract from `filterValue`. Default: `f => f.filterValue.value`. */
  getValue?: FilterValueGetter;
  /** Remap filterBy keys before building the object (e.g. `{ roleName: "name" }`). */
  keyMap?: Record<string, string>;
  /**
   * Per-key handlers for non-standard filter shapes (date ranges, numerical
   * ranges, etc.). When a handler exists for a key, it is called instead of
   * the default array-accumulation logic.
   */
  customHandlers?: Record<string, CustomFilterHandler>;
}

const defaultGetValue: FilterValueGetter = (f) => f.filterValue.value;

/**
 * Builds `Record<filterByKey, string[]>` (or mixed with range objects when
 * customHandlers are used) from a filters array, deduplicating values per key.
 *
 * @returns The filter object, or `undefined` when there are no filters.
 */
export function buildFilterByObject(
  filters: FilterEntry[],
  options?: BuildFilterByParamOptions,
): Record<string, unknown> | undefined {
  if (!filters?.length) return undefined;

  const getValue = options?.getValue ?? defaultGetValue;
  const keyMap = options?.keyMap;
  const customHandlers = options?.customHandlers;

  const filterObj: Record<string, unknown> = {};

  for (const filter of filters) {
    const resolvedKey = keyMap?.[filter.filterBy] ?? filter.filterBy;

    if (customHandlers?.[filter.filterBy]) {
      customHandlers[filter.filterBy](filter, filterObj);
      continue;
    }

    const existing = (filterObj[resolvedKey] as (string | number)[] | undefined) ?? [];
    const nextValue = getValue(filter);

    if (!existing.includes(nextValue)) {
      filterObj[resolvedKey] = [...existing, nextValue];
    } else {
      filterObj[resolvedKey] = existing;
    }
  }

  return Object.keys(filterObj).length > 0 ? filterObj : undefined;
}

/**
 * Builds the JSON string for the `filterBy` query parameter, or `undefined`
 * when there are no filters. Callers append the result to `URLSearchParams`
 * or assign it to a `Record<string, string>` params object.
 *
 * @example
 * // Simple (default getValue → filterValue.value):
 * const filterBy = buildFilterByParam(options.filters);
 *
 * // Custom value extraction:
 * const filterBy = buildFilterByParam(options.filters, {
 *   getValue: (f) => f.filterValueKey
 *     ? (f.filterValue as Record<string, any>)[f.filterValueKey]
 *     : f.filterValue.value,
 * });
 *
 * // Key remapping + conditional value:
 * const filterBy = buildFilterByParam(filters, {
 *   keyMap: { roleName: "name", modules: "module" },
 *   getValue: (f) => f.filterBy === "status" ? f.filterValue.value : f.filterValue.id,
 * });
 *
 * // Custom handlers for date ranges:
 * const filterBy = buildFilterByParam(options.filters, {
 *   customHandlers: { createdAt: handleDateRangeFilter },
 * });
 */
export function buildFilterByParam(
  filters: FilterEntry[] | undefined,
  options?: BuildFilterByParamOptions,
): string | undefined {
  if (!filters?.length) return undefined;
  const filterObj = buildFilterByObject(filters, options);
  return filterObj ? JSON.stringify(filterObj) : undefined;
}
